import * as THREE from "three";
import { ObjectTypes, TransformType } from "utils/interface.utils";
import { FONT_SIZE_MIN, FONT_SIZE_MAX } from "utils/constant.utils";
import { updateObjectInTargetImage, setIsUpdatingFromEditorWorkspace } from "store/reducers/editorSlice";
import { WorkspaceContext } from "interfaces/editor_workspace.interface";
import { applyTransformCursor } from "components/editor_workspace/scene/transform_cursor.helper";
import { renderTransformBox } from "components/editor_workspace/scene/transform_box.helper";

export const dragObjectAndUpdateScale = (workspace: WorkspaceContext) => {
    const { rendererRef, canvasRef, sceneRef, cameraRef, transformBoxRef, objectsRef, allObjectsPreviousScaleRef, selectedTransformControlRef } = workspace.refs;
    let isScaling = false;
    let scalingCorner: string | null = null;
    let initialMousePosition: THREE.Vector2 | null = null;
    let initialObjectScale: {x: number, y: number, z: number} | null = null;
    let initialCornerDistance: number | null = null;
    let initialObjectPosition: THREE.Vector3 | null = null;
    let initialBoundingBox: THREE.Box3 | null = null;
    let lastScalingObjectID: string | null = null;
    let lastScale: { x: number, y: number, z: number } | null = null;
    // Text and button sizes are derived from fontSize on every render, so a corner drag on them scales
    // fontSize instead of the mesh.
    let fontScaling: { objectID: string, initialFontSize: number } | null = null;
    // Each fontSize change rebuilds the text/button texture, so mouse moves are coalesced to one store
    // update per animation frame and repeated sizes are skipped.
    let pendingFontSize: number | null = null;
    let lastDispatchedFontSize: number | null = null;
    let fontFrameRequest: number | null = null;
    let lastPosition: THREE.Vector3 | null = null;

    const handleMouseDown = (event: MouseEvent) => {
        if (!cameraRef.current || !rendererRef.current || selectedTransformControlRef.current !== TransformType.SCALE) return;

        const mouse = new THREE.Vector2();
        const rect = rendererRef.current.domElement.getBoundingClientRect();
        mouse.x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
        mouse.y = -((event.clientY - rect.top) / rect.height) * 2 + 1;

        const mousePosition = new THREE.Vector2(
            event.clientX - rect.left,
            event.clientY - rect.top
        );
        
        if (transformBoxRef.current?.userData.detectCorner) {
            const corner = transformBoxRef.current.userData.detectCorner(mousePosition);

            if (corner) {
                // Start scaling interaction
                isScaling = true;
                scalingCorner = corner;
                initialMousePosition = mousePosition.clone();
                
                // Get the associated object
                const objectId = transformBoxRef.current.userData.associatedObjectID;
                fontScaling = getFontScalingStart(objectId);
                const objectData = objectsRef.current.get(objectId);
                
                if (objectData?.mesh) {
                    const mesh = objectData.mesh as THREE.Mesh;
                    initialObjectScale = {
                        x: mesh.scale.x,
                        y: mesh.scale.y,
                        z: mesh.scale.z
                    };

                     // Store initial position for top-left scaling
                    initialObjectPosition = mesh.position.clone();

                    mesh.updateMatrixWorld(true);
                    initialBoundingBox = new THREE.Box3().setFromObject(mesh);

                    // Calculate initial distance from center to corner for reference
                    const center = mesh.position.clone();
                    const worldCorner = getWorldCornerPosition(mesh, corner);
                    initialCornerDistance = center.distanceTo(worldCorner);
                }
                
                event.preventDefault();
                return;
            }
        }
    }

    // The resize cursor shows across the canvas while Scale is the active transform control;
    // a hovered corner picks its diagonal. For other tools this must not write.
    const updateCursorStyle = (corner: string | null) => {
        if (selectedTransformControlRef.current !== TransformType.SCALE) return;
        applyTransformCursor(canvasRef.current, { tool: TransformType.SCALE, corner, dragging: isScaling });
    };

    const handleMouseMove = (event: MouseEvent) => {
        if (!cameraRef.current || !rendererRef.current || selectedTransformControlRef.current !== TransformType.SCALE) return;
    
        const rect = rendererRef.current.domElement.getBoundingClientRect();
        const mousePosition = new THREE.Vector2(
            event.clientX - rect.left,
            event.clientY - rect.top
        );
    
        if (isScaling && scalingCorner && initialMousePosition && initialObjectScale && initialObjectPosition) {
            // Calculate scaling based on total mouse movement from initial position
            const objectId = transformBoxRef.current?.userData.associatedObjectID;
            const objectData = objectsRef.current.get(objectId);
            
            if (objectData?.mesh) {
                const scaleFactor = calculateScaleFactor(
                    initialMousePosition,
                    mousePosition,
                    scalingCorner
                );
                
                if (fontScaling) {
                    const fontSize = Math.min(FONT_SIZE_MAX, Math.max(FONT_SIZE_MIN, Math.round(fontScaling.initialFontSize * scaleFactor)));
                    pendingFontSize = fontSize;
                    if (fontFrameRequest === null) {
                        fontFrameRequest = requestAnimationFrame(flushPendingFontSize);
                    }
                    event.preventDefault();
                    return;
                }

                // Apply proportional scaling to INITIAL scale values (not current scale)
                const newScale = {
                    x: Math.max(0.1, initialObjectScale.x * scaleFactor),
                    y: Math.max(0.1, initialObjectScale.y * scaleFactor),
                    z: Math.max(0.1, initialObjectScale.z * scaleFactor)
                };

                // Update the object scale
                updateObjectScale(objectId, newScale, initialObjectScale, initialObjectPosition, initialBoundingBox, scalingCorner);
            }
            
            event.preventDefault();
            return;
        }
    
        // Handle cursor updates for corner detection
        if (transformBoxRef.current?.userData.detectCorner && selectedTransformControlRef.current === TransformType.SCALE) {
            const corner = transformBoxRef.current.userData.detectCorner(mousePosition);
            updateCursorStyle(corner);
        }
    };

    const findObject = (objectID: string) => workspace.refs.targetImageDataListRef.current[workspace.refs.selectedTargetIndexRef.current ?? 0]?.objects.find((obj) => obj.id === objectID);

    const getFontScalingStart = (objectID: string) => {
        const object = findObject(objectID);
        if (!object || (object.type !== ObjectTypes.TEXT && object.type !== ObjectTypes.BUTTON)) return null;
        const fontSize = Number(object.properties.fontSize);
        return Number.isFinite(fontSize) ? { objectID, initialFontSize: fontSize } : null;
    };

    const updateFontSizeInStore = (objectID: string, fontSize: number, commitHistory: boolean) => {
        const object = findObject(objectID);
        const targetImageID = workspace.refs.targetImageDataListRef.current[workspace.refs.selectedTargetIndexRef.current ?? 0]?.id;
        if (!object || !targetImageID) return;
        workspace.dispatch(updateObjectInTargetImage({
            targetImageID,
            object: { ...object, properties: { ...object.properties, fontSize } },
            commitHistory
        }));
        workspace.dispatch(setIsUpdatingFromEditorWorkspace(true));
    };

    const flushPendingFontSize = () => {
        fontFrameRequest = null;
        if (!fontScaling || pendingFontSize === null || pendingFontSize === lastDispatchedFontSize) return;
        lastDispatchedFontSize = pendingFontSize;
        updateFontSizeInStore(fontScaling.objectID, pendingFontSize, false);
    };

    const handleMouseUp = () => {
        if (isScaling) {
            // Final frame of the gesture — commits the whole scale/reposition as a single undo step.
            if (fontScaling) {
                if (fontFrameRequest !== null) {
                    cancelAnimationFrame(fontFrameRequest);
                    fontFrameRequest = null;
                }
                // The store ref has not caught up with the last frame's dispatch yet, so commit the
                // value the gesture ended on rather than reading it back.
                if (pendingFontSize !== null) {
                    updateFontSizeInStore(fontScaling.objectID, pendingFontSize, true);
                }
            } else if (lastScalingObjectID && lastScale && lastPosition) {
                updateObjectScaleAndPositionInStore(workspace, lastScalingObjectID, lastScale, lastPosition, true);
            }

            isScaling = false;
            fontScaling = null;
            pendingFontSize = null;
            lastDispatchedFontSize = null;
            scalingCorner = null;
            initialMousePosition = null;
            initialObjectScale = null;
            initialCornerDistance = 0;
            lastScalingObjectID = null;
            lastScale = null;
            lastPosition = null;

            updateCursorStyle(null);
            return;
        }
    }

    const calculateScaleFactor = (
        initialMouse: THREE.Vector2,
        currentMouse: THREE.Vector2,
        corner: string
    ): number => {
        // Calculate the total movement vector from initial position
        const totalMovement = currentMouse.clone().sub(initialMouse);
        
        // Calculate the diagonal distance for more natural scaling
        let scalingDistance = 0;
        
        switch (corner) {
            case 'topRight':
                // For top-right: moving right and up increases scale, left and down decreases
                scalingDistance = (totalMovement.x - totalMovement.y) / Math.sqrt(2);
                break;
            case 'topLeft':
                // For top-left: moving left and up increases scale, right and down decreases
                scalingDistance = (-totalMovement.x - totalMovement.y) / Math.sqrt(2);
                break;
            case 'bottomRight':
                // For bottom-right: moving right and down increases scale, left and up decreases
                scalingDistance = (totalMovement.x + totalMovement.y) / Math.sqrt(2);
                break;
            case 'bottomLeft':
                // For bottom-left: moving left and down increases scale, right and up decreases
                scalingDistance = (-totalMovement.x + totalMovement.y) / Math.sqrt(2);
                break;
        }
        
        // Convert pixel movement to scale factor
        // Sensitivity determines how many pixels of movement = 100% scale change
        const sensitivity = 525; // 200 pixels = 100% scale change (2x scale)
        const scaleMultiplier = 1 + (scalingDistance / sensitivity);
        
        // Ensure minimum scale of 0.1 (10% of original)
        return Math.max(0.1, scaleMultiplier);
    };
    
    // Helper function to get world position of a corner
    const getWorldCornerPosition = (mesh: THREE.Mesh, corner: string): THREE.Vector3 => {
        const boundingBox = new THREE.Box3().setFromObject(mesh);
        const size = boundingBox.getSize(new THREE.Vector3());
        const center = boundingBox.getCenter(new THREE.Vector3());
        
        const halfSize = size.clone().multiplyScalar(0.5);
        
        let cornerOffset: THREE.Vector3;
        
        switch (corner) {
            case 'topRight':
                cornerOffset = new THREE.Vector3(halfSize.x, halfSize.y, halfSize.z);
                break;
            case 'topLeft':
                cornerOffset = new THREE.Vector3(-halfSize.x, halfSize.y, halfSize.z);
                break;
            case 'bottomRight':
                cornerOffset = new THREE.Vector3(halfSize.x, -halfSize.y, halfSize.z);
                break;
            case 'bottomLeft':
                cornerOffset = new THREE.Vector3(-halfSize.x, -halfSize.y, halfSize.z);
                break;
            default:
                cornerOffset = new THREE.Vector3(0, 0, 0);
        }
        
        return center.add(cornerOffset);
    };

    // Function to update object scale and refresh transform box
    const updateObjectScale = (objectID: string, newScale: { x: number, y: number, z: number }, initialObjectScale: { x: number, y: number, z: number }, initialObjectPosition: THREE.Vector3, initialBoundingBox: THREE.Box3 | null, scalingCorner: string) => {
        const objectData = objectsRef.current.get(objectID);
        if (!objectData?.mesh || !scalingCorner) return;

        if (!initialBoundingBox) return;

        const mesh = objectData.mesh as THREE.Mesh;
        
        // Get the initial size from the bounding box
        const initialSize = initialBoundingBox.getSize(new THREE.Vector3());
        const initialCenter = initialBoundingBox.getCenter(new THREE.Vector3());
        
        // Calculate scale ratios
        const scaleRatioX = newScale.x / initialObjectScale.x;
        const scaleRatioY = newScale.y / initialObjectScale.y;
        const scaleRatioZ = newScale.z / initialObjectScale.z;
        
        // Calculate the new size after scaling
        const newSize = new THREE.Vector3(
            initialSize.x * scaleRatioX,
            initialSize.y * scaleRatioY,
            initialSize.z * scaleRatioZ
        );
        
        // Calculate size difference
        const sizeDifference = new THREE.Vector3().subVectors(newSize, initialSize);
        
        // Calculate position adjustment based on which corner is being scaled. z is never adjusted: the
        // size difference along z is the depth of the play-button plane (0.01), not a real extent, and moving
        // by it can drop a video behind the target image plane (z = 0).
        let positionAdjustment = new THREE.Vector3(0, 0, 0);
        
        switch (scalingCorner) {
            case 'topLeft':
                // When scaling from top-left, the top-left corner should stay fixed
                // So we need to move the center towards bottom-right by half the size difference
                positionAdjustment.set(
                    -sizeDifference.x * 0.5,   // Move left (opposite of growth direction)
                    sizeDifference.y * 0.5,   // Move up (opposite of growth direction)
                    0                          // z stays put: a corner drag resizes in the picture plane only
                );
                break;
            case 'topRight':
                // When scaling from top-right, the top-right corner should stay fixed
                positionAdjustment.set(
                    sizeDifference.x * 0.5,    // Move right (opposite of growth direction)
                    sizeDifference.y * 0.5,   // Move up (opposite of growth direction)
                    0                          // z stays put: a corner drag resizes in the picture plane only
                );
                break;
            case 'bottomLeft':
                // When scaling from bottom-left, the bottom-left corner should stay fixed
                positionAdjustment.set(
                    -sizeDifference.x * 0.5,   // Move left (opposite of growth direction)
                    -sizeDifference.y * 0.5,  // Move down (opposite of growth direction)
                    0                          // z stays put: a corner drag resizes in the picture plane only
                );
                break;
            case 'bottomRight':
                // When scaling from bottom-right, the bottom-right corner should stay fixed
                positionAdjustment.set(
                    sizeDifference.x * 0.5,    // Move right (opposite of growth direction)
                    -sizeDifference.y * 0.5,  // Move down (opposite of growth direction)
                    0                          // z stays put: a corner drag resizes in the picture plane only
                );
                break;
        }
        
        // Calculate the new position
        const newPosition = new THREE.Vector3().addVectors(initialObjectPosition, positionAdjustment);
        
        // Update mesh scale and position
        mesh.scale.set(newScale.x, newScale.y, newScale.z);
        mesh.position.copy(newPosition);
        
        // Update the previous scale reference
        const getPreviousObjectScale = allObjectsPreviousScaleRef.current.find(
            (object: { ID: string | null, scale: { x: number, y: number, z: number } }) => 
            object.ID === objectID
        );

        if (getPreviousObjectScale?.scale) {
            getPreviousObjectScale.scale.x = newScale.x;
            getPreviousObjectScale.scale.y = newScale.y;
            getPreviousObjectScale.scale.z = newScale.z;
        }
        
        // Re-render the transform box to match new scale and position
        if (mesh.userData.transformBox) {
            renderTransformBox(workspace, mesh);
        }
        
        // Render the scene
        if (rendererRef.current && sceneRef.current && cameraRef.current) {
            rendererRef.current.render(sceneRef.current, cameraRef.current);
        }

        // Update store with new scale and position
        lastScalingObjectID = objectID;
        lastScale = newScale;
        lastPosition = newPosition;
        updateObjectScaleAndPositionInStore(workspace, objectID, newScale, newPosition, false);
    };

    if (rendererRef.current && sceneRef.current) {
        const canvas = rendererRef.current.domElement;
        canvas.addEventListener('mousedown', handleMouseDown);
        canvas.addEventListener('mousemove', handleMouseMove);
        canvas.addEventListener('mouseup', handleMouseUp);
        
        canvas.addEventListener('mouseleave', () => {
            if (isScaling) {
                handleMouseUp();
            }
            updateCursorStyle(null);
        });
    }

    return {
        cleanup: () => {
            if (rendererRef.current) {
                const canvas = rendererRef.current.domElement;
                canvas.removeEventListener('mousedown', handleMouseDown);
                canvas.removeEventListener('mousemove', handleMouseMove);
                canvas.removeEventListener('mouseup', handleMouseUp);
                canvas.removeEventListener('mouseleave', handleMouseUp);
                
                canvas.style.cursor = 'default';
            }
        },
        isObjectBeingDraggedAndScaled: (objectID: string) => isScaling && !fontScaling && transformBoxRef.current?.userData.associatedObjectID === objectID
    }
}

export const updateObjectScaleAndPositionInStore = (workspace: WorkspaceContext, 
    objectID: string,
    newScale: { x: number, y: number, z: number },
    newPosition: THREE.Vector3,
    commitHistory: boolean = false
) => {
    const { targetImageDataListRef, selectedTargetIndexRef } = workspace.refs;
    const { dispatch } = workspace;
    const targetImageID = targetImageDataListRef.current[selectedTargetIndexRef.current ?? 0]?.id;

    if (!targetImageID || !objectID) return;

    const currentObject = targetImageDataListRef.current[selectedTargetIndexRef.current ?? 0]?.objects.find(
        (obj) => obj.id === objectID
    );

    if (!currentObject) return;

    const updatedObject = {
        ...currentObject,
        properties: {
            ...currentObject.properties,
            scaleX: newScale.x,
            scaleY: newScale.y,
            scaleZ: newScale.z,
            positionX: newPosition.x,
            positionY: newPosition.y,
            positionZ: newPosition.z
        }
    }

    // Mid-drag frames pass commitHistory:false; the final drag-end frame passes true so the
    // whole gesture collapses into a single undo step.
    dispatch(updateObjectInTargetImage({
        targetImageID: targetImageID,
        object: updatedObject,
        commitHistory
    }));
    dispatch(setIsUpdatingFromEditorWorkspace(true));
}
